/**
 * Service to open the sticker detail overlay from anywhere in the app.
 * The overlay is rendered at app root (app.component) and appears full-screen
 * above header and navbar (z-index: 10000).
 *
 * Usage:
 *   constructor(private stickerOverlay: StickerDetailOverlayService) {}
 *
 *   openStickerDetail() {
 *     this.stickerOverlay.open({
 *       items: this.v2SectionItems,
 *       sections: this.itemsBySection,
 *       currentSectionIndex: 0,
 *       selectedIndex: 0,
 *       collectionDetails: this.userCollectionDetails,
 *       iconLocationPrefix: this.userCollectionDetails?.icon_location_prefix ?? '',
 *       isMobile: this.platform.is('mobile'), // hides reel column; gestures + album FAB remain
 *       iconColor: this.currentItem?.icon_color ?? '',
 *       callbacks: {
 *         onClosed: () => { ... },
 *         onItemChange: (e) => { ... },
 *         onPlayVideo: (e) => { ... },
 *         onSectionChangeRequest: (dir) => { ... },
 *         onFooterAction: (e) => { ... },
 *       },
 *     });
 *   }
 */
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import {
  V2DetailOverlayItem,
  V2DetailOverlaySection,
  V2DetailOverlayCollectionDetails,
  V2DetailViewMode,
} from '../components/v2-detail-overlay/v2-detail-overlay.component';

export interface StickerDetailOverlayCallbacks {
  onClosed?: () => void;
  onItemChange?: (payload: { index: number; item: V2DetailOverlayItem }) => void;
  onPlayVideo?: (payload: { index: number; item: V2DetailOverlayItem }) => void;
  onSectionChangeRequest?: (dir: 'next' | 'prev') => void;
  onVideoEnded?: () => void;
  onFooterAction?: (payload: { action: string; item: V2DetailOverlayItem | null }) => void;
  /** Inline `<video>` loading / ready / error (from `app-v2-detail-overlay` `videoLoadState`). */
  onVideoLoadState?: (payload: { state: 'loading' | 'ready' | 'error' }) => void;
  /** Header back in video view — return to slide view (do not close overlay). */
  onVideoBack?: () => void;
  /** Open album view on the page that contains this sticker (parent closes sticker overlay first). */
  onOpenAlbum?: (payload: { itemId: string }) => void;
}

export interface StickerDetailOverlayConfig {
  items: V2DetailOverlayItem[];
  sections: V2DetailOverlaySection[];
  currentSectionIndex: number;
  selectedIndex: number;
  collectionDetails: V2DetailOverlayCollectionDetails | null;
  iconLocationPrefix?: string;
  viewMode?: V2DetailViewMode;
  showVideo?: boolean;
  showFooter?: boolean;
  isMobile?: boolean;
  iconColor?: string;
  videoSrc?: string;
  aspectRatio?: 'LANDSCAPE' | 'PORTRAIT';
  hasVideoLoaded?: boolean;
  /** When true, reel column shows “open album” (gesture collections only; parent supplies callback). */
  showOpenAlbumButton?: boolean;
  callbacks?: StickerDetailOverlayCallbacks;
}

export interface StickerDetailOverlayState extends Omit<StickerDetailOverlayConfig, 'callbacks'> {
  isOpen: boolean;
  callbacks?: StickerDetailOverlayCallbacks;
}

@Injectable({
  providedIn: 'root',
})
export class StickerDetailOverlayService {
  private readonly initialState: StickerDetailOverlayState = {
    isOpen: false,
    items: [],
    sections: [],
    currentSectionIndex: 0,
    selectedIndex: 0,
    collectionDetails: null,
    iconLocationPrefix: '',
    viewMode: 'slideView',
    showVideo: true,
    showFooter: true,
    isMobile: false,
    iconColor: '',
    videoSrc: '',
    aspectRatio: 'PORTRAIT',
    hasVideoLoaded: false,
    showOpenAlbumButton: false,
    callbacks: undefined,
  };

  private readonly stateSubject = new BehaviorSubject<StickerDetailOverlayState>(
    this.initialState
  );

  readonly state$: Observable<StickerDetailOverlayState> =
    this.stateSubject.asObservable();

  /** Open the overlay with the given config. Can be called from anywhere in the app. */
  open(config: Partial<StickerDetailOverlayConfig> & { callbacks?: StickerDetailOverlayCallbacks }): void {
    const { callbacks, ...rest } = config;
    this.stateSubject.next({
      ...this.initialState,
      ...rest,
      callbacks,
      isOpen: true,
    });
  }

  /** Close the overlay. */
  close(): void {
    this.stateSubject.next({
      ...this.stateSubject.value,
      isOpen: false,
    });
  }

  /** Update overlay state (e.g. when switching sections, view mode, etc.) */
  update(partial: Partial<StickerDetailOverlayState>): void {
    const current = this.stateSubject.value;
    if (!current.isOpen) return;
    this.stateSubject.next({ ...current, ...partial });
  }

  get isOpen(): boolean {
    return this.stateSubject.value.isOpen;
  }

  get viewMode(): V2DetailViewMode {
    return this.stateSubject.value.viewMode ?? 'slideView';
  }

  get callbacks(): StickerDetailOverlayCallbacks | undefined {
    return this.stateSubject.value.callbacks;
  }
}
